import clsx from 'clsx';
import * as styles from '../styles/containers/DashboardEditor.less';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { withTranslation, WithTranslation } from "react-i18next";
import { DASHBOARD_TYPES, DASHBOARD_URL, DEFAULT_RULES, RATING_BUTTON_DEFAULT_VALUES, RATING_BUTTON_HIDE } from '../constants/config';
import Error from '../components/common/Error';
import Loader from '../components/common/Loader';
import DashboardTargets from '../components/dashboard/DashboardTargets';
import DashboardCheckbox from '../components/dashboard/DashboardCheckbox';
import DashboardSelect from '../components/dashboard/DashboardSelect';
import DashboardActions from '../components/dashboard/DashboardActions';
import TimeRangePanel from '../components/dashboard/TimeRangePanel';
import Timestamps from '../components/common/Timestamps';
import SuperuserIsActive from './SuperuserIsActive';
import moment from 'moment';
import DashboardUsers from '../components/dashboard/DashboardUsers';
import { useActions } from '../hooks/useActions';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/configureStore';

interface DashboardEditorOwnProps {
  className?: string;
}

// Rating-button visibility flags shared by ratingBtnsRef and hideBtns.
interface RatingButtonFlags {
  status: boolean;
  comments: boolean;
  star: boolean;
  hide: boolean;
  close: boolean;
  publish: boolean;
  like: boolean;
}

//-------------------------------------------------------------
// Update loading status based on request reducers store updates
// and loading list requests. If any request type is true
// then the loading status is true.
//-------------------------------------------------------------
const isLoadingByList = (_store: RootState, _list: string[]): boolean => {
  for (let i = 0, ilen = _list.length; i < ilen; i++) {
    if (_store.loading[_list[i]] === true) {
      return true;
    }
  }
  return false;
};

const dashboardLoadingList = [
  'REQUEST_DASHBOARD_TARGETS',
  'REQUEST_DASHBOARD_RULES',
  'REQUEST_DASHBOARD',
  'REQUEST_USER_CUSTOMERS',
  'REQUEST_USER_TARGETS',
];

const DashboardEditor = (props: DashboardEditorOwnProps & WithTranslation) => {
  const { t } = props; // [t, i18n]
  const actions = useActions();
  const params = useParams();
  const navigate = useNavigate();
  // The async mount chain (below) needs always-fresh redux values the way the
  // old class read `this.props` inside its promise callbacks. Selector closures
  // would be stale there, so we read `store.getState()` at call time instead.
  const store = useStore<RootState>();

  const dashboardIsLoading = useAppSelector((s) => isLoadingByList(s, dashboardLoadingList));
  // The form's checkboxes are UNCONTROLLED (defaultChecked), so they read redux
  // exactly once, at mount. The mount effect below seeds redux after its
  // requests resolve, so the form must not mount until that seeding has run —
  // otherwise the boxes bake in whatever was there first. While the requests
  // were sequential this happened to hold; firing them in parallel made it a
  // race, and the boxes showed their values only sometimes.
  const [isSeeding, setIsSeeding] = useState(true);
  const error = useAppSelector((s) => s.dashboard.error);
  const errorMessage = useAppSelector((s) => s.dashboard.errorMessage);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isSuperuser = useAppSelector((s) => s.auth.isSuperuser);
  const isDashboardValid = useAppSelector((s) => s.dashboard.isDashboardValid);
  const authenticated = useAppSelector((s) => s.dashboard.authenticated);
  const customerId = useAppSelector((s) => s.dashboard.customerId || 1);
  const dashboardCustomerName = useAppSelector((s) => s.dashboard.dashboardCustomerName);
  const dashboardTargets = useAppSelector((s) => s.dashboard.dashboardTargets);
  const disabled = useAppSelector((s) => s.dashboard.disabled);
  const email = useAppSelector((s) => s.dashboard.email);
  const layout = useAppSelector((s) => s.dashboard.layout);
  const type = useAppSelector((s) => s.dashboard.type);
  const name = useAppSelector((s) => s.dashboard.name);
  const createdAt = useAppSelector((s) => s.dashboard.createdAt);
  const updatedAt = useAppSelector((s) => s.dashboard.updatedAt);
  const selectedTargets = useAppSelector((s) => s.dashboard.selectedTargets);
  const showEmpty = useAppSelector((s) => s.dashboard.showEmpty);
  const showFeedback = useAppSelector((s) => s.dashboard.showFeedback);
  const showFlsChart = useAppSelector((s) => s.dashboard.showFlsChart);
  const showFlsCustomerChart = useAppSelector((s) => s.dashboard.showFlsCustomerChart);
  const showFlsEmployeeChart = useAppSelector((s) => s.dashboard.showFlsEmployeeChart);
  const showOverviewIcon = useAppSelector((s) => s.dashboard.showOverviewIcon);
  const showHistoryChart = useAppSelector((s) => s.dashboard.showHistoryChart);
  const showImage = useAppSelector((s) => s.dashboard.showImage);
  const showCommentsBtn = useAppSelector((s) => s.dashboard.showCommentsBtn);
  const showStarBtn = useAppSelector((s) => s.dashboard.showStarBtn);
  const showHideBtn = useAppSelector((s) => s.dashboard.showHideBtn);
  const showCloseBtn = useAppSelector((s) => s.dashboard.showCloseBtn);
  const showPublishBtn = useAppSelector((s) => s.dashboard.showPublishBtn);
  const showLikeBtn = useAppSelector((s) => s.dashboard.showLikeBtn);
  const enableAnalytics = useAppSelector((s) => s.dashboard.enableAnalytics);
  const showNpsChart = useAppSelector((s) => s.dashboard.showNpsChart);
  const showENpsChart = useAppSelector((s) => s.dashboard.showENpsChart);
  const showNpsDisChart = useAppSelector((s) => s.dashboard.showNpsDisChart);
  const showPieChart = useAppSelector((s) => s.dashboard.showPieChart);
  const emailCommentsEnabled = useAppSelector((s) => s.dashboard.emailCommentsEnabled);
  const emailCommentsBcc = useAppSelector((s) => s.dashboard.emailCommentsBcc);
  const showPlsChart = useAppSelector((s) => s.dashboard.showPlsChart);
  const timeFrom = useAppSelector((s) => s.dashboard.timeFrom);
  const timeTo = useAppSelector((s) => s.dashboard.timeTo);
  const changeTarget = useAppSelector((s) => s.dashboard.changeTarget);
  const themeId = useAppSelector((s) => s.dashboard.themeId);
  const showDisabled = useAppSelector((s) => s.dashboard.showDisabled);
  const showStatus = useAppSelector((s) => s.dashboard.showStatus);
  const notificationLink = useAppSelector((s) => s.dashboard.notificationLink);
  const isSuperuserActive = useAppSelector((s) => s.auth.isSuperuserActive);

  const [showDialog, setShowDialog] = useState(false);

  // The rating-button checkboxes are UNCONTROLLED (defaultChecked), same as the
  // chart checkboxes in the left column — so a click toggles natively and
  // instantly. (Controlled `checked` would make React snap the box back to the
  // last committed value until the next commit; this form is huge and double-
  // renders under StrictMode in dev, so that commit is ~1s away → visible lag.)
  // ratingBtnsRef holds the seeded values for `defaultChecked` at mount; on a
  // Type/Layout change we update the DOM imperatively via setDomChecked below.
  // Each box's redux value (what Save reads) is still written in its onChange.
  const ratingBtnsRef = useRef<RatingButtonFlags>({
    status: false, comments: false, star: false, hide: false, close: false, publish: false, like: false,
  });
  // `disabled` can change on Type/Layout; kept as state (the disabled attribute
  // has no snap-back problem, and Type/Layout changes are rare).
  const [hideBtns, setHideBtns] = useState<RatingButtonFlags & { header: boolean }>({
    status: false, comments: false, star: false, hide: false, close: false, publish: false, like: false, header: false,
  });

  // Imperatively reflect a rating-button value in the (already mounted) DOM.
  const setDomChecked = (id: string, value: boolean) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) {
      el.checked = value;
    }
  };

  // Data gathered by the mount chain, surfaced in render/handlers. These were
  // instance fields; as refs they persist without themselves forcing a render
  // (the accompanying redux dispatches re-render and pick up the current value).
  const themesListRef = useRef<any[]>([]);
  const dashboardCustomersRef = useRef<any[]>([]);
  const dashboardCustomerNameRef = useRef<string | undefined>(undefined);
  const customerIdRef = useRef<number | undefined>(undefined);   // this._customerId (from the logged-in user)
  const themeIdRef = useRef<string | undefined>(undefined);
  const themesRef = useRef<any>(undefined);
  const rulesRef = useRef<any>(undefined);
  const dashboardTargetsRef = useRef<any[]>([]);
  const currentThemeLabelRef = useRef<string | undefined>(undefined);
  const showDisabledRef = useRef<boolean | undefined>(undefined);
  const notificationLinkRef = useRef<boolean | undefined>(undefined);

  // Debounced dispatch, for TEXT inputs only — its job is to coalesce
  // keystrokes. It is a SINGLE shared debounce, so two different fields written
  // within 250ms collapse into one dispatch carrying only the last arguments.
  // Discrete selections (dropdowns, the layout cascade) use fastEditDashboard:
  // they fire once per user action, and the Theme and Type dropdowns are
  // controlled off redux, so a debounced write makes the selection visibly lag.
  const editDashboard = useMemo(() => debounce(actions.editDashboard, 250), [actions]);
  useEffect(() => () => editDashboard.cancel(), [editDashboard]);
  const fastEditDashboard = actions.editDashboard;

  //-------------------------------------------------------------
  // Build the theme list for the Select from already-fetched themes.
  //
  // This used to fetch the themes itself, as the last link of the mount chain.
  // The fetch now starts up front with the other independent requests and the
  // result is handed in here — the mapping needs dashboard.themeId, which only
  // exists once the rules have landed, so only this part is ordered.
  //-------------------------------------------------------------
  const applyThemes = (themes: any[]) => {
    const list = themes.map((theme, i) => ({
      value: theme.id,
      label: theme.name,
      index: i,
      config: theme.config,
    }));
    themesListRef.current = list;
    const currentThemeId = store.getState().dashboard.themeId;
    let currentThemeIndex = 0;
    for (let i = 0, ilen = list.length; i < ilen; i++) {
      if (currentThemeId === list[i].value) {
        currentThemeIndex = i;
        break;
      }
    }
    currentThemeLabelRef.current = list[currentThemeIndex] ? list[currentThemeIndex].label : undefined;
  };

  // Existing dashboard: seed the checkbox state from redux. New dashboard:
  // seed it from the RATING_BUTTON_DEFAULT_VALUES 'default' row (and return that
  // row so the caller can also dispatch the defaults to redux).
  const initRatingButtonCheckboxes = (newDashboard: boolean) => {
    if (newDashboard) {
      const index = RATING_BUTTON_DEFAULT_VALUES.findIndex(x => x.type === 'default');
      const d = RATING_BUTTON_DEFAULT_VALUES[index];
      ratingBtnsRef.current = {
        status: d.status, comments: d.comments, star: d.star, hide: d.hide,
        close: d.close, publish: d.publish, like: d.like,
      };
      return d;
    }
    const state = store.getState().dashboard;
    ratingBtnsRef.current = {
      status: state.showStatus,
      comments: state.showCommentsBtn,
      star: state.showStarBtn,
      hide: state.showHideBtn,
      close: state.showCloseBtn,
      publish: state.showPublishBtn,
      like: state.showLikeBtn,
    };
    const currentType = state.type;
    const currentLayout = state.layout;
    const index = RATING_BUTTON_HIDE.findIndex(x => x.type === currentType && x.layout === currentLayout);
    if (index >= 0) {
      const h = RATING_BUTTON_HIDE[index];
      setHideBtns({
        status: h.status, comments: h.comments, star: h.star, hide: h.hide,
        close: h.close, publish: h.publish, like: h.like, header: h.header,
      });
    }
    return undefined;
  };

  //-------------------------------------------------------------
  // Force react-datepicker input text boxes size to 100%
  //-------------------------------------------------------------
  const adjustTimeRangeInput = () => {
    const nodes = document.getElementsByClassName('time-range-input');
    for (let j = 0, jlen = nodes.length; j < jlen; j++) {
      const elements = nodes[j].getElementsByTagName('input');
      for (let i = 0, ilen = elements.length; i < ilen; i++) {
        elements[i].style.width = '100%';
      }
    }
  };

  const updateFeedbackButtons = (btnType: string | null, btnLayout: string | null, updateOnlyVisibilities?: boolean) => {
    if (!updateOnlyVisibilities) {
      const index = RATING_BUTTON_DEFAULT_VALUES.findIndex(x => x.type === btnType && x.layout === btnLayout);
      if (index >= 0) {
        const d = RATING_BUTTON_DEFAULT_VALUES[index];
        ratingBtnsRef.current = {
          status: d.status, comments: d.comments, star: d.star, hide: d.hide,
          close: d.close, publish: d.publish, like: d.like,
        };
        // The form is mounted here, so reflect the new defaults in the DOM.
        setDomChecked('tickbox_show_status', d.status);
        setDomChecked('tickbox_show_comments_btn', d.comments);
        setDomChecked('tickbox_show_star_btn', d.star);
        setDomChecked('tickbox_show_hide_btn', d.hide);
        setDomChecked('tickbox_show_close_btn', d.close);
        setDomChecked('tickbox_show_publish_btn', d.publish);
        setDomChecked('tickbox_show_like_btn', d.like);
        fastEditDashboard('showStatus', d.status);
        fastEditDashboard('showCommentsBtn', d.comments);
        fastEditDashboard('showStarBtn', d.star);
        fastEditDashboard('showHideBtn', d.hide);
        fastEditDashboard('showCloseBtn', d.close);
        fastEditDashboard('showPublishBtn', d.publish);
        fastEditDashboard('showLikeBtn', d.like);
      }
    }

    const hideButtonIndex = RATING_BUTTON_HIDE.findIndex(x => x.type === btnType && x.layout === btnLayout);
    if (hideButtonIndex >= 0) {
      const h = RATING_BUTTON_HIDE[hideButtonIndex];
      setHideBtns({
        status: h.status, comments: h.comments, star: h.star, hide: h.hide,
        close: h.close, publish: h.publish, like: h.like, header: h.header,
      });
    }
  };

  // componentWillMount: fetch targets → dashboard targets → dashboard → rules,
  // then load themes/customers and seed the rating-button checkboxes. Runs once
  // (the class did this in componentWillMount, before the first paint).
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSeeding(false);
      return;
    }
    dashboardTargetsRef.current = [];
    const dashboardId = params.dashboardId || false;

    actions.resetDashboard();

    console.log(isSuperuser ? 'Your role: Superuser' : 'Your role: Admin');

    // Every request below is independent — dashboardId comes from the URL, and
    // none of the endpoints consumes another's result. They used to run in a
    // .then() chain, so opening a dashboard cost FIVE sequential round-trips
    // (names -> children -> dashboard -> rules -> customers/themes) before any
    // data appeared. Now they all start at once and only the post-processing is
    // ordered, since that genuinely does depend on the responses.
    const userTargetsPromise = actions.fetchUserTargets();
    const dashboardTargetsPromise = actions.fetchDashboardTargets();
    const customersPromise = actions.fetchUserCustomers();
    const themesPromise = actions.fetchThemes('*');
    const dashboardPromise = dashboardId
      ? actions.fetchDashboard(dashboardId)
      : Promise.resolve(null);
    const rulesPromise = dashboardId
      ? actions.fetchDashboardRules(dashboardId)
      : Promise.resolve(null);

    // fetchDashboard and fetchDashboardRules write disjoint slices of
    // state.dashboard (name/email/selectedTargets/timestamps vs the 37 rules
    // fields), so whichever lands first does not clobber the other.
    Promise.all([
      userTargetsPromise,
      dashboardTargetsPromise,
      customersPromise,
      themesPromise,
      dashboardPromise,
      rulesPromise,
    ]).then(([names, fetchedTargets, customers, themes, dashboard, rules]: any[]) => {
      const user = names.payload.user;
      if (typeof user.customerId === 'number') {
        customerIdRef.current = user.customerId;
      }
      dashboardTargetsRef.current = fetchedTargets;

      // Needs the dashboard's name/email/selectedTargets in the store.
      if (dashboard) {
        actions.validateDashboard();
      }

      // Build the customer dropdown list once; both branches below use it.
      const customerList = customers.payload.map((customer: any) => ({
        value: customer.id,
        label: customer.name,
        themes: customer.themes,
      }));

      if (dashboardId) {
        if (!rules || !rules.payload) {
          console.error('Hit empty dashboard rules:%o', dashboardId);
          setIsSeeding(false);
          return;
        }
        rulesRef.current = rules.payload;

        // Both read state.dashboard, so they must follow the rules response.
        applyThemes(themes);
        initRatingButtonCheckboxes(false);

        // Match the dashboard's customer to pick up its name and theme list.
        if (rulesRef.current && rulesRef.current.customer_id) {
          const id = rulesRef.current.customer_id;
          const matched = customers.payload.find((c: any) => c.id === id);
          if (matched) {
            dashboardCustomerNameRef.current = matched.name;
            themesRef.current = matched.themes.split(',');
            fastEditDashboard('dashboardCustomerName', dashboardCustomerNameRef.current);
          }
        }
        dashboardCustomersRef.current = customerList;
        setIsSeeding(false);
        return;
      }

      // New dashboard: seed it from the signed-in user's customer.
      console.info('Init new dashboard.');
      // initTheme() used to run only in the existing-dashboard branch, so the
      // theme dropdown was empty when creating a dashboard. It is populated for
      // both now.
      applyThemes(themes);
      dashboardCustomersRef.current = customerList;
      rulesRef.current = DEFAULT_RULES;
      if (customerIdRef.current) {
        const matched = customerList.find((c: any) => c.value === customerIdRef.current);
        if (matched) {
          dashboardCustomerNameRef.current = matched.label;
          rulesRef.current.customer_id = customerIdRef.current;
          themesRef.current = matched.themes.split(',');
          themeIdRef.current = themesRef.current[0]; // Customer default theme is the 1st in list
          const defaults = initRatingButtonCheckboxes(true);
          // fastEditDashboard, NOT editDashboard: the latter is a single shared
          // 250ms debounce meant for coalescing keystrokes. Six calls in a row
          // collapse to ONE dispatch carrying only the last arguments, so five
          // of these seed values were being silently dropped.
          fastEditDashboard('customerId', customerIdRef.current);
          fastEditDashboard('dashboardCustomerName', dashboardCustomerNameRef.current);
          // Email is deliberately NOT seeded: the creator is not necessarily the
          // recipient, and it gates the Save button, so pre-filling it would let
          // a dashboard be saved with an address nobody chose. The original code
          // did seed it, but the shared 250ms debounce silently dropped the call
          // (six rapid editDashboard() calls collapse to one, keeping only the
          // last arguments), so in practice the field has always been empty.
          fastEditDashboard('themeId', themeIdRef.current);
          fastEditDashboard('showDisabled', showDisabledRef.current);
          fastEditDashboard('showStatus', defaults ? defaults.status : undefined);
          fastEditDashboard('notificationLink', notificationLinkRef.current);
        }
      }
      setIsSeeding(false);
    }).catch((e: any) => {
      // Never leave the editor stuck behind the loader if a request fails; the
      // error itself is surfaced by the reducers' error state.
      console.error('Dashboard load failed:%O', e);
      setIsSeeding(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only by design (was componentWillMount)

  // componentDidMount: size the time-range inputs once mounted.
  useEffect(() => {
    adjustTimeRangeInput();
  }, []); // mount-only

  // useCallback so the identity is stable: DashboardTarget is React.memo'd, and a
  // fresh callback on every render would re-render every row on every toggle,
  // which is exactly what the memo exists to prevent. `actions` is memoised on
  // dispatch, so this is created once.
  const onTargetToggle = useCallback((targetId: string) => {
    actions.toggleSelectedTarget(targetId);
  }, [actions]);

  const onSaveDashboard = () => {
    const dashboardId = params.dashboardId || false;
    const customerName = dashboardCustomerName || dashboardCustomerNameRef.current; // TODO
    console.info('onSaveDashboard -  customerId:%O', customerId);
    console.info('onSaveDashboard -  dashboardCustomerName:%O', dashboardCustomerName);
    console.info('onSaveDashboard -  name:%O', name);
    console.info('onSaveDashboard - email:%O', email);
    console.info('onSaveDashboard - rules:%O', rulesRef.current);
    console.info('showHistoryChart:%O', showHistoryChart);
    console.info('    showPieChart:%O', showPieChart);
    console.info('    showNpsChart:%O', showNpsChart);
    console.info('    showENpsChart:%O', showENpsChart);
    console.info('    showNpsDisChart:%O', showNpsDisChart);
    console.info('    showFlsChart:%O', showFlsChart);
    console.info('    showFlsCustomerChart:%O', showFlsCustomerChart);
    console.info('    showFlsEmployeeChart:%O', showFlsEmployeeChart);
    console.info('    showOverviewIcon:%O', showOverviewIcon);
    console.info('    showPlsChart:%O', showPlsChart);
    console.info('    showFeedback:%O', showFeedback);
    console.info('    showEmpty:%O', showEmpty);
    console.info('    showImage:%O', showImage);
    console.info('    showCommentsBtn:%O', showCommentsBtn);
    console.info('    showStarBtn:%O', showStarBtn);
    console.info('    showHideBtn:%O', showHideBtn);
    console.info('    showCloseBtn:%O', showCloseBtn);
    console.info('    showPublishBtn:%O', showPublishBtn);
    console.info('    showLikeBtn:%O', showLikeBtn);
    console.info('    enableAnalytics:%O', enableAnalytics);
    console.info('+layout:%O', layout);
    console.info('+type:%O', type);
    console.info('    disabled:%O', disabled);
    console.info('    authenticated:%O', authenticated);
    console.info('emailCommentsEnabled:%O', emailCommentsEnabled);
    console.info('+showDisabled:%O', showDisabled);
    console.info('+showStatus:%O', showStatus);
    console.info('++notificationLink:%O', notificationLink);
    console.info('emailCommentsBcc:%O', emailCommentsBcc);
    console.info('timeFrom:%O', timeFrom);
    console.info('timeTo:%O', timeTo);
    console.info('changeTarget:%O', changeTarget);
    console.info('themeId:%O', themeId);

    // Default rules as base rules.
    const defaultRules = DEFAULT_RULES as Record<string, any>;
    const newRules: Record<string, any> = {};
    for (const key in defaultRules) {
      newRules[key] = defaultRules[key];
    }

    if (rulesRef.current) {
      console.info('We have rules:%O.', rulesRef.current);
      for (const key in defaultRules) {
        if (typeof rulesRef.current[key] !== 'undefined' && rulesRef.current[key] !== null) {
          console.warn('New rules:%O%O', key, typeof rulesRef.current[key]);
          if (newRules[key] !== rulesRef.current[key]) {
            console.warn('Rule:[%O][%O], default value:[%O] => set value [%O]', key, typeof newRules[key], newRules[key], rulesRef.current[key]);
            newRules[key] = rulesRef.current[key];
          }
        }
      }
    } else {
      console.info('We have default rules');
    }

    console.info('Initial rules:%O', newRules);
    if (newRules) {

      delete newRules.created_at;
      delete newRules.hash;

      newRules.show_history_chart = showHistoryChart;
      newRules.show_pie_chart = showPieChart;
      newRules.show_fls_chart = showFlsChart;
      newRules.show_fls_customer_chart = showFlsCustomerChart;
      newRules.show_fls_employee_chart = showFlsEmployeeChart;
      newRules.show_overview_icon = showOverviewIcon;
      newRules.show_nps_chart = showNpsChart;
      newRules.show_enps_chart = showENpsChart;
      newRules.show_nps_dis_chart = showNpsDisChart;
      newRules.show_feedback = showFeedback;
      newRules.show_empty = showEmpty;
      newRules.show_image = showImage;
      newRules.show_comments_btn = showCommentsBtn;
      newRules.show_star_btn = showStarBtn;
      newRules.show_hide_btn = showHideBtn;
      newRules.show_close_btn = showCloseBtn;
      newRules.show_publish_btn = showPublishBtn;
      newRules.show_like_btn = showLikeBtn;
      newRules.enable_analytics = enableAnalytics;
      newRules.layout = layout;
      newRules.type = type;
      newRules.disabled = disabled;
      newRules.authentication = authenticated;
      newRules.name = name;
      newRules.customer_id = customerId;
      newRules.email_comments_enabled = emailCommentsEnabled;
      newRules.email_comments_bcc = emailCommentsBcc;
      newRules.show_disabled = showDisabled;
      newRules.show_status = showStatus;
      newRules.notification_link = notificationLink;
      newRules.show_pls_chart = showPlsChart;
      newRules.time_from = timeFrom || '-infinity';
      newRules.time_to = timeTo || 'infinity';
      newRules.change_target = changeTarget || false;
      newRules.theme_id = themeId || 1;

      // Sanity check for all newRules
      for (const key in defaultRules) {
        if (newRules[key] == null || typeof newRules[key] == 'undefined') {
          if (newRules[key] !== defaultRules[key]) {
            console.warn('%O value is [%O][%O], fixed with[%O].', key, typeof newRules[key], newRules[key], defaultRules[key]);
            newRules[key] = defaultRules[key];
          }
        }
      }
    } else { // Create new dashboard with default rules.
      console.error('No rules. Do nothing.');
      return;
    }

    const dashboard = {
      customerName,
      customerId,
      name,
      email,
      targets: selectedTargets,
      _id: dashboardId,
      rules: newRules
    };

    console.info('==> onSaveDashboard ==> dashboard:%O', dashboard);

    const action = dashboardId ?
      actions.updateDashboard(dashboard) :
      actions.saveNewDashboard(dashboard);

    // On update we already know the id; on create take it from the response.
    // NOTE (plan bug #6): `response._id` is UNVERIFIED — the new-API create response's
    // id field must be confirmed in the manual browser test (CustomerEditor's turned
    // out to be `response.id`). Left as-is to keep this conversion behavior-preserving.
    action.then((response: any) => navigate('/dashboard/' + (dashboardId || (response && response._id))));
  };

  const onChangeDashboardCustomerName = (e: any) => {
    const id = e.value;
    const label = e.label;
    let nextThemeId = 1;
    if (nextThemeId && typeof nextThemeId === 'string') {
      nextThemeId = e.themes.split(',')[0]; // Select the 1st
    }
    console.log('+++ onChangeDashboardCustomerName, id:%O, name:%O default themeId:%O', id, label, nextThemeId);
    customerIdRef.current = id;
    dashboardCustomerNameRef.current = label;
    fastEditDashboard('dashboardCustomerName', label);
    fastEditDashboard('themeId', nextThemeId);
    fastEditDashboard('customerId', id);
  };

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    editDashboard('name', e.target.value.trim());
  };

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    editDashboard('email', e.target.value.trim());
  };

  const onChangeEmailCommentsBcc = (e: React.ChangeEvent<HTMLInputElement>) => {
    editDashboard('emailCommentsBcc', e.target.value.trim());
  };

  // Checkbox handlers read the toggled value from the change event (`e.target.checked`)
  // rather than `getElementById(id).checked`. The boxes stay UNCONTROLLED (defaultChecked)
  // so the click toggles natively and instantly — controlled `checked` gates the visual on
  // the whole editor re-rendering (it subscribes to ~50 dashboard fields), which is a
  // measurable lag. See [[track2-rating-buttons-uncontrolled]].
  const onChangeShowHistoryChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowHistoryChart - showHistoryChart:%O', e.target.checked);
    editDashboard('showHistoryChart', e.target.checked);
  };
  const onChangeShowPieChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowPieChart - showPieChart:%O', e.target.checked);
    editDashboard('showPieChart', e.target.checked);
  };
  const onChangeShowFlsChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowFlsChart - showFlsChart:%O', e.target.checked);
    editDashboard('showFlsChart', e.target.checked);
  };
  const onChangeShowFlsCustomerChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeshowFlsCustomerChart - showFlsCustomerChart:%O', e.target.checked);
    editDashboard('showFlsCustomerChart', e.target.checked);
  };
  const onChangeShowFlsEmployeeChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowFlsEmployeeChart - showFlsEmployeeChart:%O', e.target.checked);
    editDashboard('showFlsEmployeeChart', e.target.checked);
  };
  const onChangeShowOverviewIcon = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowOverviewIcon - showOverviewIcon:%O', e.target.checked);
    editDashboard('showOverviewIcon', e.target.checked);
  };
  const onChangeShowNpsChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowNpsChart - showNpsChart:%O', e.target.checked);
    editDashboard('showNpsChart', e.target.checked);
  };
  const onChangeShowENpsChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowENpsChart - showENpsChart:%O', e.target.checked);
    editDashboard('showENpsChart', e.target.checked);
  };
  const onChangeShowNpsDisChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowNpsDisChart - showNpsDisChart:%O', e.target.checked);
    editDashboard('showNpsDisChart', e.target.checked);
  };
  const onChangeShowFeedback = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowFeedback - showFeedback:%O', e.target.checked);
    editDashboard('showFeedback', e.target.checked);
  };
  const onChangeShowEmpty = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowEmpty - showEmpty:%O', e.target.checked);
    editDashboard('showEmpty', e.target.checked);
  };
  const onChangeShowImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowImage - enabled:%O', e.target.checked);
    editDashboard('showImage', e.target.checked);
  };
  const onChangeShowCommentsBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowCommentsBtn - enabled:%O', e.target.checked);
    editDashboard('showCommentsBtn', e.target.checked);
    ratingBtnsRef.current.comments = e.target.checked;
  };
  const onChangeShowStarBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowStarBtn - enabled:%O', e.target.checked);
    editDashboard('showStarBtn', e.target.checked);
    ratingBtnsRef.current.star = e.target.checked;
  };
  const onChangeShowHideBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowHideBtn - enabled:%O', e.target.checked);
    editDashboard('showHideBtn', e.target.checked);
    ratingBtnsRef.current.hide = e.target.checked;
  };
  const onChangeShowCloseBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowCloseBtn - enabled:%O', e.target.checked);
    editDashboard('showCloseBtn', e.target.checked);
    ratingBtnsRef.current.close = e.target.checked;
  };
  const onChangeShowPublishBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowPublishBtn - enabled:%O', e.target.checked);
    editDashboard('showPublishBtn', e.target.checked);
    ratingBtnsRef.current.publish = e.target.checked;
  };
  const onChangeShowLikeBtn = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowLikeBtn - enabled:%O', e.target.checked);
    editDashboard('showLikeBtn', e.target.checked);
    ratingBtnsRef.current.like = e.target.checked;
  };
  const onChangeEnableAnalytics = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeEnableAnalytics - enabled:%O', e.target.checked);
    editDashboard('enableAnalytics', e.target.checked);
  };
  const onChangeLayout = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextLayout: string | null = e.target.checked ? 'public' : null;
    if (nextLayout === 'public') {
      updateFeedbackButtons(type, nextLayout);
    } else {
      updateFeedbackButtons(type, nextLayout, true);
    }
    console.info('onChangeLayout - layout:%O', nextLayout);
    fastEditDashboard('layout', nextLayout);
  };
  const onChangeDisabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeDisabled - disabled:%O', e.target.checked);
    editDashboard('disabled', e.target.checked);
  };
  const onChangeShowDisabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowDisabled - showDisabled:%O', e.target.checked);
    editDashboard('showDisabled', e.target.checked);
    showDisabledRef.current = e.target.checked;
  };
  const onChangeShowStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('+showStatus:%O', e.target.checked);
    editDashboard('showStatus', e.target.checked);
    ratingBtnsRef.current.status = e.target.checked;
  };

  const onChangeNotificationLink = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('+++notificationLink:%O', e.target.checked);
    editDashboard('notificationLink', e.target.checked);
    notificationLinkRef.current = e.target.checked;
  };

  const onChangeAuthenticated = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeAutheticated - authenticated:%O', e.target.checked);
    editDashboard('authenticated', e.target.checked);
  };
  const onChangeEmailCommentsEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeEmailCommentsEnabled - enabled:%O', e.target.checked);
    editDashboard('emailCommentsEnabled', e.target.checked);
  };
  const onChangeShowPlsChart = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.info('onChangeShowPlsChart - showPlsChart:%O', e.target.checked);
    editDashboard('showPlsChart', e.target.checked);
  };
  //---------------------------------------------
  // Convert date (now) to midnight string
  //---------------------------------------------
  const convertToTimeString = (time: moment.MomentInput) => {
    let timeString = moment(time).format();
    timeString = timeString.split('T')[0] + 'T00:00:00.000Z'; // *** TODO *** admin TZ handling
    return timeString;
  };
  const onChangeTimeFrom = (time: Date | number | null) => {
    const timeFromStr = convertToTimeString(time);
    console.info('onChangeTimeFrom:%O', timeFromStr);
    editDashboard('timeFrom', timeFromStr);
  };
  const onChangeTimeTo = (time: Date | number | null) => {
    const timeToStr = convertToTimeString(time);
    console.info('onChangeTimeTo:%O', timeToStr);
    editDashboard('timeTo', timeToStr);
  };
  const onChangeTimeFromInfinity = (infinity: boolean) => {
    console.info('onChangeTimeFromInfinity:%O', infinity);
    if (infinity) {
      editDashboard('timeFrom', '-infinity');
    } else {
      console.log('+ set from inifinity');
      onChangeTimeFrom(Date.now());
    }
  };
  const onChangeTimeToInfinity = (infinity: boolean) => {
    console.info('onChangeTimeToInfinity:%O', infinity);
    if (infinity) {
      editDashboard('timeTo', 'infinity');
    } else {
      console.log('+ set from inifinity');
      onChangeTimeTo(Date.now());
    }
  };
  const onChangeTheme = (e: any) => {
    const nextThemeId = e.value;
    const index = e.index;
    console.info('onChangeTheme:%O:%O', nextThemeId, themesListRef.current[index] && themesListRef.current[index].label);
    fastEditDashboard('themeId', nextThemeId);
  };
  const onChangeType = (e: any) => {
    const index = e.value;
    const typeName = DASHBOARD_TYPES[index].label;
    const nextType = DASHBOARD_TYPES[index].type;
    console.info('onChangeType:%O:%O:%O', index, typeName, nextType);
    updateFeedbackButtons(nextType, layout);
    fastEditDashboard('type', nextType);
  };

  const onDeleteClick = () => setShowDialog(true);

  const onDeleteConfirm = () => {
    if (!disabled) {
      return;
    }
    const dashboardId = params.dashboardId || false;
    actions.deleteDashboard(dashboardId)
      .then((response: any) => {
        navigate('/');
      });
    setShowDialog(false);
  };

  const onDeleteCancel = () => setShowDialog(false);

  if (error) {
    return (
      <Error message={errorMessage} />
    );
  }

  const dashboardId = params.dashboardId || false;

  const isLoading = dashboardIsLoading || isSeeding;
  if (isLoading) {
    console.debug('Loading...[%O]', isLoading);
    return (
      <Loader style={{ padding: "2em", textAlign: "center" }} className={props.className} />
    );
  } else {
    console.debug('Loading ...Ready:[%O]', isLoading);
  }

  // The Type dropdown's selected option: the DASHBOARD_TYPES row matching the
  // current `type` (null when unset), passed straight to react-select as `value`.
  const selectedTypeOption = (typeof type === "string" ? DASHBOARD_TYPES.find(x => x.type === type) : undefined) ?? null;

  // The theme dropdown showed the current theme through `placeholder`, which
  // react-select renders in its muted placeholder colour — so it looked greyer
  // than the Type dropdown beside it, which passes a real `value`. Passing the
  // matching option makes it render as a selected value, leaving grey to mean
  // what it should: nothing chosen.
  const selectedThemeOption = themesListRef.current.find(x => x.value === themeId) ?? null;

  return (
    <div className={clsx(styles['dashboard--editor'], 'pure-u-24-24')} >
      <h2>{t('main.dashboardEditor')}
        {isLoading &&
          <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
      </h2>
      <SuperuserIsActive>
        <div className="pure-u-14-24">
          <div style={{
            border: "1px solid red",
            padding: "0.5rem",
          }}>
            <div className="pure-u-4-24" style={{ verticalAlign: "justify" }}>{t('main.customer')}</div>
            <div className="pure-u-12-24">
              <DashboardSelect
                placeholder="Select to change..."
                value={{ label: dashboardCustomerNameRef.current, value: customerIdRef.current, themeId: themeIdRef.current }}
                options={dashboardCustomersRef.current}
                onChange={onChangeDashboardCustomerName}
              />
            </div>
          </div>
          <div style={{ height: "0.4em" }}></div>
        </div>
      </SuperuserIsActive>
      <label className={styles.label}>
        {t('dashboard.editor.name')} <input
          className={styles.input}
          type="text"
          placeholder="Dashboard name"
          defaultValue={name}
          onChange={onChangeName} />
        <a className={dashboardId ? styles['dashboard--link'] : styles.hidden}
          // Update cache when opening dashboard
          href={`${DASHBOARD_URL}?hash=${dashboardId}&cache=del`}
          title="Open dashboard"
          target="_blank"><i className="fa fa-lg fa-external-link" aria-hidden="true"></i></a>
      </label>
      <label className={(dashboardId && !isSuperuserActive) ? styles.hidden : styles.label}>
        {t('main.email')} <input
          className={styles.input}
          type={(dashboardId && !isSuperuserActive) ? 'hidden' : 'email'}
          placeholder="Dashboard email"
          defaultValue={email}
          onChange={onChangeEmail} />
      </label>
      <label className={(dashboardId && (!isSuperuserActive || !emailCommentsEnabled)) ? styles.hidden : styles.label}>
        {t('dashboard.editor.commentsbcc')}<input
          className={styles.input}
          type={(dashboardId && (!isSuperuserActive && !emailCommentsEnabled)) ? 'hidden' : 'email'}
          placeholder="Rating comments email Bcc"
          defaultValue={emailCommentsBcc}
          onChange={onChangeEmailCommentsBcc}
        />
      </label>

      <div className={styles.dashboardCheckboxes}>
        <div className="pure-u-1-24"></div>
        <div className="pure-u-18-24">

          <div className="chartSelector">
            <div className="pure-u-8-24">
              <label className={styles.label}>{t('dashboard.editor.chartVisibility')}:</label>
            </div>
            <div className="pure-u-8-24">
              <label className={styles.label}>{t('dashboard.editor.feedbackVisibility')}:</label>
            </div>
            <div className="pure-u-8-24">
              <label className={styles.label}>{t('dashboard.editor.buttonVisibility')}:</label>
            </div>
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_history_chart"
              title="Display History Chart"
              label={t('dashboard.editor.chart.showHistory')}
              defaultChecked={showHistoryChart}
              onChange={onChangeShowHistoryChart}
            />
            <DashboardCheckbox
              id="tickbox_feedback"
              title="Display Show Feedback"
              label={t('dashboard.editor.feedback.show.feedback')}
              defaultChecked={showFeedback}
              onChange={onChangeShowFeedback}
            />
            <DashboardCheckbox
              id="tickbox_show_status"
              title="Show status"
              label={t('dashboard.editor.ratingButtons.showStatus')}
              defaultChecked={ratingBtnsRef.current.status}
              onChange={onChangeShowStatus}
              disabled={hideBtns.status}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_pie_chart"
              title="Display Pie Chart"
              label={t('dashboard.editor.chart.showPie')}
              defaultChecked={showPieChart}
              onChange={onChangeShowPieChart}
            />
            <DashboardCheckbox
              id="tickbox_show_image"
              title="Enable images"
              label={t('dashboard.editor.showImage')}
              defaultChecked={showImage}
              onChange={onChangeShowImage}
            />
            <DashboardCheckbox
              id="tickbox_show_comments_btn"
              title="Display Show Comments Button"
              label={t('dashboard.editor.ratingButtons.showCommentsBtn')}
              defaultChecked={ratingBtnsRef.current.comments}
              onChange={onChangeShowCommentsBtn}
              disabled={hideBtns.comments}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_fls_chart"
              title="Display Feelings Chart"
              label={t('dashboard.editor.chart.showFeelings')}
              defaultChecked={showFlsChart}
              onChange={onChangeShowFlsChart}
            />
            <DashboardCheckbox
              id="tickbox_show_empty"
              title="Display Show Empty"
              label={t('dashboard.editor.feedback.show.empty')}
              defaultChecked={showEmpty}
              onChange={onChangeShowEmpty}
            />
            <DashboardCheckbox
              id="tickbox_show_star_btn"
              title="Display Show Star Button"
              label={t('dashboard.editor.ratingButtons.showStarBtn')}
              defaultChecked={ratingBtnsRef.current.star}
              onChange={onChangeShowStarBtn}
              disabled={hideBtns.star}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_fls_customer_chart"
              title="Display Customer Feelings Chart"
              label={t('dashboard.editor.chart.showCustomerFeelings')}
              defaultChecked={showFlsCustomerChart}
              onChange={onChangeShowFlsCustomerChart}
            />
            <DashboardCheckbox
              id="tickbox_show_disabled"
              title="Show disabled targets ratings"
              label={t('dashboard.editor.showDisabled')}
              defaultChecked={showDisabled}
              onChange={onChangeShowDisabled}
            />
            <DashboardCheckbox
              id="tickbox_show_hide_btn"
              title="Display Show Hide Button"
              label={t('dashboard.editor.ratingButtons.showHideBtn')}
              defaultChecked={ratingBtnsRef.current.hide}
              onChange={onChangeShowHideBtn}
              disabled={hideBtns.hide}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_fls_employee_chart"
              title="Display Employee Feelings Chart"
              label={t('dashboard.editor.chart.showEmployeeFeelings')}
              defaultChecked={showFlsEmployeeChart}
              onChange={onChangeShowFlsEmployeeChart}
            />
            <div className="pure-u-8-24"></div>
            <DashboardCheckbox
              id="tickbox_show_close_btn"
              title="Display Show Close Button"
              label={t('dashboard.editor.ratingButtons.showCloseBtn')}
              defaultChecked={ratingBtnsRef.current.close}
              onChange={onChangeShowCloseBtn}
              disabled={hideBtns.close}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_nps_chart"
              title="Display NPS Chart"
              label={t('dashboard.editor.chart.showNPS')}
              defaultChecked={showNpsChart}
              onChange={onChangeShowNpsChart}
            />
            <DashboardCheckbox
              id="tickbox_enable_analytics"
              title="Enable Analytics Ordering"
              label={t('dashboard.editor.enableAnalytics')}
              defaultChecked={enableAnalytics}
              onChange={onChangeEnableAnalytics}
            />
            <DashboardCheckbox
              id="tickbox_show_publish_btn"
              title="Display Show Publish Button"
              label={t('dashboard.editor.ratingButtons.showPublishBtn')}
              defaultChecked={ratingBtnsRef.current.publish}
              onChange={onChangeShowPublishBtn}
              disabled={hideBtns.publish}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_nps_dis_chart"
              title="Display NPS Distribution"
              label={t('dashboard.editor.chart.showNPSDis')}
              defaultChecked={showNpsDisChart}
              onChange={onChangeShowNpsDisChart}
            />
            <DashboardCheckbox
              id="tickbox_layout"
              title="Dashboard layout"
              label={t('dashboard.editor.layout.public')}
              defaultChecked={!!layout}
              onChange={onChangeLayout}
            />
            <DashboardCheckbox
              id="tickbox_show_like_btn"
              title="Display Show Like Button"
              label={t('dashboard.editor.ratingButtons.showLikeBtn')}
              defaultChecked={ratingBtnsRef.current.like}
              onChange={onChangeShowLikeBtn}
              disabled={hideBtns.like}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_enps_chart"
              title="Display eNPS Chart"
              label={t('dashboard.editor.chart.showENPS')}
              defaultChecked={showENpsChart}
              onChange={onChangeShowENpsChart}
            />
            <DashboardCheckbox
              id="tickbox_authenticated"
              title="Dashboard authenticated"
              label={t('dashboard.editor.authenticated')}
              defaultChecked={authenticated}
              onChange={onChangeAuthenticated}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_pls_chart"
              title="Display Plus Chart"
              label={t('dashboard.editor.chart.showPlus')}
              defaultChecked={showPlsChart}
              onChange={onChangeShowPlsChart}
            />
            <DashboardCheckbox
              id="tickbox_disabled"
              title="Disable dashboard access"
              label={t('dashboard.editor.disabled')}
              defaultChecked={disabled}
              onChange={onChangeDisabled}
            />
          </div>

          <div className="chartSelector">
            <DashboardCheckbox
              id="tickbox_overview_icon"
              title="Display Overview Icon"
              label={t('dashboard.editor.chart.showOverviewIcon')}
              defaultChecked={showOverviewIcon}
              onChange={onChangeShowOverviewIcon}
            />
          </div>

          <SuperuserIsActive>
            <div style={{
              border: "1px solid red",
              padding: "0.5rem",
            }}>
              <div className="chartSelector">
                <DashboardCheckbox
                  id="tickbox_email_comments_enabled"
                  title="Email comments enabled"
                  label={t('dashboard.editor.comments.email')}
                  defaultChecked={emailCommentsEnabled}
                  onChange={onChangeEmailCommentsEnabled}
                />
                <DashboardCheckbox
                  id="tickbox_notification_link"
                  title="Notification link"
                  label={t('dashboard.editor.notificationLink')}
                  defaultChecked={notificationLink}
                  onChange={onChangeNotificationLink}
                />

              </div>

              <TimeRangePanel
                timeFrom={timeFrom}
                timeTo={timeTo}
                onToggleFromInfinity={onChangeTimeFromInfinity}
                onToggleToInfinity={onChangeTimeToInfinity}
                onChangeTimeFrom={onChangeTimeFrom}
                onChangeTimeTo={onChangeTimeTo}
                onCollapseChange={adjustTimeRangeInput}
              />
              <div className="pure-u-2-24" style={{ verticalAlign: "justify", paddingTop: "20px" }}>{t('dashboard.editor.theme')}</div>
              <div className="pure-u-12-24" style={{ paddingTop: "20px" }}>
                <DashboardSelect
                  placeholder={currentThemeLabelRef.current}
                  id="select_dashboard_theme"
                  options={themesListRef.current}
                  value={selectedThemeOption}
                  onChange={onChangeTheme}
                />
              </div>
              <div></div>
              <div className="pure-u-2-24" style={{ verticalAlign: "justify", paddingTop: "20px" }}>{t('dashboard.editor.type')}</div>
              <div className="pure-u-12-24" style={{ paddingTop: "20px" }}>
                <DashboardSelect
                  placeholder="Default"
                  id="select_dashboard_type"
                  options={DASHBOARD_TYPES}
                  value={selectedTypeOption}
                  onChange={onChangeType}
                />
              </div>
            </div>
          </SuperuserIsActive>
        </div>
      </div>
      <DashboardActions
        isLoading={isLoading}
        isDashboardValid={isDashboardValid}
        disabled={disabled}
        showDialog={showDialog}
        name={name}
        onSave={onSaveDashboard}
        onDeleteClick={onDeleteClick}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
      />
      <Timestamps createdAt={createdAt} updatedAt={updatedAt} />
      <div style={{ display: "flex" }}>
        <div>
          <h2>{t('dashboard.editor.availableTargets')}
            {isLoading &&
              <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
          </h2>
          <DashboardTargets
            targets={dashboardTargets as any}
            selectedTargets={selectedTargets as any}
            onTargetToggle={onTargetToggle} />
        </div>
        <div>
          <h2>{t('dashboard.editor.users')}
            {isLoading &&
              <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
          </h2>
          <DashboardUsers
            customerId={customerId}
          />
        </div>
      </div>
      <button className={clsx(isLoading ? styles.hidden : styles.save, 'button success')}
        onClick={onSaveDashboard}
        disabled={!isDashboardValid}
        >{t('button.save')}</button>
    </div>
  );
};

export default withTranslation('common')(DashboardEditor);
